import { readdir, readFile } from "node:fs/promises";
import { join, resolve } from "node:path";
import { atomicWriteJson, isEnoent } from "./_fs.js";
import { ChapterSchema } from "../data-model/schema.js";
import type { Chapter } from "../data-model/novel.js";
import type { Logger } from "../utils/logger.js";
import { silentLogger } from "../utils/logger.js";

export interface ChapterStore {
  save(chapter: Chapter): Promise<void>;
  /** Returns chapters in ascending index order. */
  loadAll(): Promise<readonly Chapter[]>;
}

export class MemoryChapterStore implements ChapterStore {
  readonly #map = new Map<number, Chapter>();
  async save(chapter: Chapter): Promise<void> {
    this.#map.set(Number(chapter.index), chapter);
  }
  async loadAll(): Promise<readonly Chapter[]> {
    return [...this.#map.values()].sort((a, b) => Number(a.index) - Number(b.index));
  }
}

export class FilesystemChapterStore implements ChapterStore {
  readonly #logger: Logger;
  readonly #dir: string;

  constructor(dir: string, opts: { logger?: Logger } = {}) {
    this.#dir = resolve(dir);
    this.#logger = opts.logger ?? silentLogger;
  }

  async save(chapter: Chapter): Promise<void> {
    await atomicWriteJson(join(this.#dir, `${Number(chapter.index)}.json`), chapter);
  }

  async loadAll(): Promise<readonly Chapter[]> {
    let entries: string[];
    try {
      entries = await readdir(this.#dir);
    } catch (err) {
      if (isEnoent(err)) return [];
      throw err;
    }
    const chapters: Chapter[] = [];
    for (const name of entries) {
      if (!/^\d+\.json$/.test(name)) continue;
      const path = join(this.#dir, name);
      // Resume's source-of-truth contract (see downloader.ts comment in the
      // resume-load branch) requires that one corrupt or half-written file
      // doesn't poison the entire reuse set. Skip+log instead of throwing.
      let text: string;
      try {
        text = await readFile(path, "utf8");
      } catch (err) {
        this.#logger.warn({ path, err }, "chapter-store: failed to read file, skipping");
        continue;
      }
      let raw: unknown;
      try {
        raw = JSON.parse(text);
      } catch (err) {
        this.#logger.warn({ path, err }, "chapter-store: invalid JSON, skipping");
        continue;
      }
      const parsed = ChapterSchema.safeParse(raw);
      if (!parsed.success) {
        this.#logger.warn(
          { path, issues: parsed.error.issues },
          "chapter-store: schema mismatch, skipping",
        );
        continue;
      }
      chapters.push(parsed.data);
    }
    chapters.sort((a, b) => Number(a.index) - Number(b.index));
    return chapters;
  }
}
